'use client';

import { TFunction } from 'i18next';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { ButtonClose } from '@/components/buttons';
import { TooltipTypes, useTooltipStore } from '@/store/tooltipStore';

import { ScTooltipGlobal } from './styled';

function isFunction(value: any): value is (t: TFunction) => string {
  return typeof value === 'function';
}

export const GlobalTooltip: FC = () => {
  const { t } = useTranslation();
  const {
    successCircleIcon: SuccessCircleIcon,
    infoIcon: InfoIcon,
    warningIcon: WarningIcon,
    errorIcon: ErrorIcon,
  } = useTheme();

  const tooltip = useTooltipStore.use.tooltip();
  const remove = useTooltipStore.use.remove();

  useEffect(() => {
    if (tooltip && tooltip.animate) {
      const timeout = setTimeout(() => {
        remove();
      }, tooltip.removeTimeout);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [tooltip, remove]);

  const body = useMemo(() => {
    if (tooltip?.translate) {
      if (isFunction(tooltip.body)) {
        return tooltip.body(t);
      }
      if (typeof tooltip.body === 'string') {
        return t(tooltip.body);
      }
    } else {
      return tooltip?.body;
    }
  }, [tooltip, t]);

  if (tooltip == null) return null;

  const icon = {
    [TooltipTypes.SUCCESS]: <SuccessCircleIcon />,
    [TooltipTypes.INFO]: <InfoIcon />,
    [TooltipTypes.WARNING]: <WarningIcon />,
    [TooltipTypes.ERROR]: <ErrorIcon />,
  }[tooltip.type];

  return (
    <ScTooltipGlobal
      key={tooltip?.id}
      success={tooltip.type === TooltipTypes.SUCCESS}
      warning={tooltip?.type === TooltipTypes.WARNING}
      info={tooltip.type === TooltipTypes.INFO}
      error={tooltip.type === TooltipTypes.ERROR}
      animate={!!tooltip?.animate}
      removeTimeout={tooltip?.removeTimeout || 0}
      onClick={remove}
    >
      <ButtonClose top={0} right={0} gray onClick={remove} />
      {icon && <div className="icon">{icon}</div>}
      {body}
    </ScTooltipGlobal>
  );
};
